import convexHull from "convex-hull";

// Detect browser to apply appropriate marker size
const isChrome = () => {
  const userAgent = navigator.userAgent.toLowerCase();
  return userAgent.includes("chrome") && !userAgent.includes("edg");
};

// Calculate marker size based on browser and device pixel ratio
// Note: plotGlPixelRatio is set to min 2x for quality, so we compensate marker sizes
const getMarkerSize = () => {
  const pixelRatio = window.devicePixelRatio || 1;
  // Rendering pixel ratio is always at least 2x for quality
  const renderPixelRatio = Math.max(pixelRatio, 2);

  if (isChrome()) {
    // Chrome renders markers larger than Edge
    // Desktop (ratio=1, render=2): 4/2=2, scaled 2x = 4 visually
    // Laptop (ratio=2, render=2): 6/2=3, scaled 2x = 6 visually
    if (pixelRatio >= 2) {
      return 6 / renderPixelRatio;
    } else {
      return 4 / renderPixelRatio;
    }
  }

  // Edge and other browsers
  // Desktop (ratio=1, render=2): 8/2=4, scaled 2x = 8 visually
  // Laptop (ratio=2, render=2): 8/2=4, scaled 2x = 8 visually
  if (pixelRatio >= 2) {
    return 6 / renderPixelRatio;
  } else {
    return 4 / renderPixelRatio;
  }
};

export function plotlyGamutGraphTreaceData(input, points = [], view = "both") {
  /**
   * supporse to be virginResinGamut=input.gamut.LAB_V
   * supporse to be virginResinGamut=input.gamut.LAB_R
   */

  let virginResinGamut = [];
  let recycleResinGamut = [];

  virginResinGamut = input?.gamut?.virginResinGamut;
  recycleResinGamut = input?.gamut?.recycleResinGamut;

  let virginResinGamut_xyz = {
    x: [],
    y: [],
    z: [],
  };

  let recycleResinGamut_xyz = {
    x: [],
    y: [],
    z: [],
  };

  let colorPoints_graph = {
    cordinates: {
      x: [],
      y: [],
      z: [],
    },
    rgb: [],
  };

  /* 
  be careful, the sequency of points in the array of gamuts or color points are l, a, and b
  when ploting the graph, x is a, y is b, z is l
  */
  virginResinGamut.map((element) => {
    virginResinGamut_xyz.x.push(element[1]); //a
    virginResinGamut_xyz.y.push(element[2]); //b
    virginResinGamut_xyz.z.push(element[0]); //l
  });

  recycleResinGamut.map((element) => {
    recycleResinGamut_xyz.x.push(element[1]); //a
    recycleResinGamut_xyz.y.push(element[2]); //b
    recycleResinGamut_xyz.z.push(element[0]); //l
  });

  if (points.length > 0) {
    // Visible indexes from array
    input.target_color.map((element, index) => {
      if (points[index] || points[0] === "All") {
        colorPoints_graph.cordinates.x.push(element.LAB[1]);
        colorPoints_graph.cordinates.y.push(element.LAB[2]);
        colorPoints_graph.cordinates.z.push(element.LAB[0]);
        colorPoints_graph.rgb.push(
          `rgb(${element.RGB[0]},${element.RGB[1]},${element.RGB[2]})`
        );
      }
    });
  }

  const recycledGamut = {
    alphahull: 0,
    opacity: 0.4,
    type: "mesh3d",
    color: "gray",
    lighting: {
      diffuse: 0.01,
    },
    x: recycleResinGamut_xyz.x,
    y: recycleResinGamut_xyz.y,
    z: recycleResinGamut_xyz.z,
  };

  const virginGamut = {
    alphahull: 0,
    opacity: 0.15,
    type: "mesh3d",
    color: "gray",
    lighting: {
      diffuse: 0.01,
    },
    x: virginResinGamut_xyz.x,
    y: virginResinGamut_xyz.y,
    z: virginResinGamut_xyz.z,
  };

  const data = [
    {
      x: colorPoints_graph.cordinates.x,
      y: colorPoints_graph.cordinates.y,
      z: colorPoints_graph.cordinates.z,
      mode: "markers",
      type: "scatter3d",
      marker: {
        color: colorPoints_graph.rgb,
        size: getMarkerSize(),
        symbol: "circle",
      },
    },
  ];

  if (view === "both") {
    data.push(recycledGamut);
    data.push(virginGamut);
  } else if (view === "recycled") {
    data.push(recycledGamut);
  } else if (view === "virgin") {
    data.push(virginGamut);
  }

  return data;
}

// Function to calculate the size of gamut
export const calculateSizeOfGamut = (LAB) => {
  let sizeOfGamut = 0;
  const points = LAB.map(([L, A, B]) => [L, A, B]);
  const vLmin = Math.min(...LAB.map((v) => v[0]));
  const vLmax = Math.max(...LAB.map((v) => v[0]));
  const lABcntr = [[(vLmin + vLmax) / 2, 0, 0]];

  /* Get convex hull with simplices */
  const hull = convexHull(points);
  const LAB1 = hull.map((k) => LAB[k[0]]);
  const LAB2 = hull.map((k) => LAB[k[1]]);
  const LAB3 = hull.map((k) => LAB[k[2]]);
  const nrOfTri = hull.length;

  for (let i = 0; i < nrOfTri; i++) {
    const vA = [lABcntr[0][1], lABcntr[0][2], lABcntr[0][0]];
    const vB = [LAB1[i][1], LAB1[i][2], LAB1[i][0]];
    const vC = [LAB2[i][1], LAB2[i][2], LAB2[i][0]];
    const vD = [LAB3[i][1], LAB3[i][2], LAB3[i][0]];

    const vAB = vB.map((val, index) => val - vA[index]);
    const vAC = vC.map((val, index) => val - vA[index]);
    const vAD = vD.map((val, index) => val - vA[index]);

    const crossProduct = crossProducts(vAC, vAD);
    const dotProduct = dotProducts(vAB, crossProduct);

    const volume = Math.abs(dotProduct) / 6;
    sizeOfGamut += volume;
  }

  sizeOfGamut = Math.round(sizeOfGamut);
  return sizeOfGamut;
};

const crossProducts = (vAC, vAD) => {
  return [
    vAC[1] * vAD[2] - vAC[2] * vAD[1],
    vAC[2] * vAD[0] - vAC[0] * vAD[2],
    vAC[0] * vAD[1] - vAC[1] * vAD[0],
  ];
};

const dotProducts = (vAB, crossProduct) => {
  let result = 0;
  for (let i = 0; i < vAB.length; i++) {
    result += vAB[i] * crossProduct[i];
  }
  return result;
};

export const getContrastColorFromRgb = (r, g, b) => {
  // Calculate the luminance of the color from formula
  const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  // If luminance is high, return black (rgb(0, 0, 0)), otherwise return white (rgb(255, 255, 255))
  return luminance > 128 ? 0 : 255;
};

export const resinMaterialDetails = (requirements) => {
  let details = `${requirements?.thickness}mm ${requirements?.ldr}% in ${requirements?.recycledResinConc}%`;
  let resinName = "";
  let batch = "";
  if (requirements?.selectedRecycledResin?.sub_class) {
    resinName = `${requirements?.selectedRecycledResin?.sub_class}-${requirements.selectedRecycledResin?.grad}`;
    if (requirements.selectedRecycledResin?.batch !== "unknown") {
      batch = requirements.selectedRecycledResin?.batch;
    }
  }
  if (requirements?.resinRequirement?.newResinRecycled?.resinName) {
    resinName = requirements.resinRequirement.newResinRecycled.resinName;
    batch = requirements.resinRequirement.newResinRecycled.batchNumber;
  }

  return `${details} ${resinName} ${batch}`;
};

export const virginResinMaterialDetails = (requirements) => {
  let details = requirements?.resinClass;
  if (requirements?.resinRequirement?.newResinvirgin?.resinName) {
    details = `${details} ${requirements?.resinRequirement?.newResinvirgin?.resinName}`;
  } else {
    details = `${details} ${requirements?.selectedVirginResin?.sub_class}`;
  }

  return details;
};

export const removeHtmlEntitiesAndTags = (inputString) => {
  if (typeof inputString !== "string") {
    return ""; // Handle non-string input
  }
  // 1. Decode HTML entities (like &amp;, &lt;, &gt;, etc.)
  const decodedString = (function () {
    const txt = document.createElement("textarea");
    txt.innerHTML = inputString;
    return txt.value;
  })();

  // 2. Remove HTML tags
  let cleanedString = decodedString.replace(/<[^>]*>/g, "");

  return cleanedString;
};

export const sliceString = (str, chunkSize) => {
  const lines = [];
  for (let i = 0; i < str.length; i += chunkSize) {
    lines.push(str.slice(i, i + chunkSize));
  }
  return lines;
};

/* Function  for calculate the delta E values */

export const calculateDeltaE = (L1, L2, a1, a2, b1, b2) => {
  if (
    typeof L1 !== "number" ||
    typeof a1 !== "number" ||
    typeof b1 !== "number" ||
    typeof L2 !== "number" ||
    typeof a2 !== "number" ||
    typeof b2 !== "number"
  ) {
    return "Invalid input. All values must be numbers.";
  }

  const deltaL = L1 - L2;
  const deltaA = a1 - a2;
  const deltaB = b1 - b2;

  const deltaE = Math.sqrt(deltaL * deltaL + deltaA * deltaA + deltaB * deltaB);

  return deltaE.toFixed(2);
};

export const validateNewForm = (event) => {
  // Iterate over each key-value pair in the eventect
  for (const key in event) {
    const value = event[key];
    // Skip validation for empty string values
    if (value === "") {
      continue;
    }

    // Check if the value is an object with 'required' and 'highlight' keys
    if (typeof value === "object" && value !== null) {
      if (value.required === false && value.highlight === false) {
        // This is a valid sub-object, continue to the next key
        continue;
      } else {
        // One of the conditions is not false, so the form is invalid
        return false;
      }
    } else {
      // The value is not an object and not an empty string, so it's an invalid case
      return false;
    }
  }
  // All keys have been validated successfully
  return true;
};
